import React, { useState, useEffect } from 'react'
import { Modal, Button, Row, Col, Badge, Form, Alert, Spinner } from 'react-bootstrap'
import {
  useAdminAnalyticData,
  useUpdateAdminAnalyticData
} from 'api/hooks/use-admin-analytics-data'
import { useAdminTags } from 'api/hooks/use-admin-tags'

const AnalyticModal = ({ analyticId, mode, onClose, onSaved }) => {
  const show = !!analyticId
  const isEditing = mode === 'edit'
  const { data: analytic, isLoading } = useAdminAnalyticData({ id: analyticId })
  const { data: tags } = useAdminTags({ options: { enabled: isEditing } })
  const updateMutation = useUpdateAdminAnalyticData({
    id: analyticId,
    options: { onSuccess: () => { onSaved?.(); onClose() } }
  })

  const [formData, setFormData] = useState({})

  useEffect(() => {
    if (analytic && isEditing) {
      setFormData({
        key: analytic.key || '',
        name: analytic.name || '',
        description: analytic.description || '',
        description_template: analytic.description_template || '',
        risk: analytic.risk || '',
        filters: analytic.filters || '',
        code: analytic.code || '',
        is_core: analytic.is_core || false,
        risk_score: analytic.risk_score || 0,
        tag_ids: analytic.tags?.map((t) => t.id) || []
      })
    }
  }, [analytic, isEditing])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleTagToggle = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter((id) => id !== tagId)
        : [...prev.tag_ids, tagId]
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    updateMutation.mutate(formData)
  }

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Analytic' : 'View Analytic'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : !analytic ? (
          <Alert variant="warning">Analytic not found.</Alert>
        ) : isEditing ? (
          <Form onSubmit={handleSubmit}>
            {updateMutation.error && (
              <Alert variant="danger" className="mb-3">{updateMutation.error.message}</Alert>
            )}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Key</Form.Label>
                  <Form.Control type="text" name="key" value={formData.key} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Code</Form.Label>
                  <Form.Control type="text" name="code" value={formData.code} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Risk Score</Form.Label>
                  <Form.Control type="number" step="0.1" name="risk_score" value={formData.risk_score} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3 mt-4">
                  <Form.Check type="checkbox" name="is_core" label="On" checked={formData.is_core} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description Template</Form.Label>
              <Form.Control as="textarea" rows={2} name="description_template" value={formData.description_template} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Risk</Form.Label>
              <Form.Control as="textarea" rows={2} name="risk" value={formData.risk} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Filters</Form.Label>
              <Form.Control as="textarea" rows={2} name="filters" value={formData.filters} onChange={handleChange} />
            </Form.Group>
            {tags && (
              <Form.Group className="mb-3">
                <Form.Label>Tags</Form.Label>
                <div>
                  {tags.map((tag) => (
                    <Badge
                      key={tag.id}
                      bg={formData.tag_ids?.includes(tag.id) ? 'primary' : 'light'}
                      text={formData.tag_ids?.includes(tag.id) ? 'white' : 'dark'}
                      className="me-2 mb-2"
                      style={{ cursor: 'pointer', border: '1px solid #dee2e6' }}
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </Form.Group>
            )}
            <div className="d-flex justify-content-end">
              <Button variant="light-primary" onClick={onClose} className="me-2">Cancel</Button>
              <Button type="submit" variant="primary" disabled={updateMutation.isLoading}>
                {updateMutation.isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </Form>
        ) : (
          <>
            <Row>
              <Col md={6}>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Key:</dt>
                  <dd className="col-sm-7"><code>{analytic.key}</code></dd>
                  <dt className="col-sm-5">Name:</dt>
                  <dd className="col-sm-7">{analytic.name}</dd>
                  <dt className="col-sm-5">Code:</dt>
                  <dd className="col-sm-7">{analytic.code || 'N/A'}</dd>
                  <dt className="col-sm-5">Status:</dt>
                  <dd className="col-sm-7"><span className="text-muted">{analytic.is_core ? 'On' : 'Off'}</span></dd>
                  <dt className="col-sm-5">Risk Score:</dt>
                  <dd className="col-sm-7">{analytic.risk_score}</dd>
                  <dt className="col-sm-5">Tags:</dt>
                  <dd className="col-sm-7">
                    {analytic.tags?.length > 0
                      ? analytic.tags.map((tag) => (
                          <Badge key={tag.id} pill className="border border-light text-primary bg-white me-1">{tag.name}</Badge>
                        ))
                      : 'None'}
                  </dd>
                </dl>
              </Col>
              <Col md={6}>
                <h6>Description</h6>
                <p>{analytic.description || 'N/A'}</p>
                <h6>Risk</h6>
                <p>{analytic.risk || 'N/A'}</p>
                <h6>Description Template</h6>
                <p>{analytic.description_template || 'N/A'}</p>
                <h6>Filters</h6>
                <p>{analytic.filters || 'N/A'}</p>
              </Col>
            </Row>
            {analytic.analytic_table_columns?.length > 0 && (
              <>
                <hr />
                <h6>Table Columns</h6>
                {analytic.analytic_table_columns.map((atc) => (
                  <Badge key={atc.id} pill className="border border-light text-primary bg-white me-1">
                    {atc.table_column_name} ({atc.placeholder_key})
                  </Badge>
                ))}
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default AnalyticModal
